import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { createChat } from "@n8n/chat";
import "@n8n/chat/style.css";
import "./chatStyles.css";
import { adjustColorBrightness } from "./chatUtils";
import { setChatInitialized, setLastMerchantId, resetChat } from "../../store/slices/chatSlice";

const ChatWidget = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const merchant = useSelector((state) => state.configStore.merchant);
  const { isInitialized, lastMerchantId } = useSelector((state) => state.chatStore);
  const isLoggedIn = useSelector((state) => state.userStore.isLoggedIn);
  const currentLanguage = i18n.language;
  const prevLoggedInRef = useRef(isLoggedIn);

  useEffect(() => {
    // Only initialize once per session and merchant
    if (isInitialized && lastMerchantId === merchant?.id) return;

    const existingChat = document.querySelector("[data-n8n-chat]");
    if (existingChat) return;

    const primaryColor = merchant?.primary_color || "#4F46E5";
    const primaryShade50 = merchant?.primary_color
      ? adjustColorBrightness(merchant.primary_color, -10)
      : "#6366F1";
    const primaryShade100 = merchant?.primary_color
      ? adjustColorBrightness(merchant.primary_color, -20)
      : "#7C3AED";

    const chatConfig = {
      webhookUrl: "https://hesham035.app.n8n.cloud/webhook/e972c191-1668-4433-87e7-c4fe52cf3f27/chat",
      mode: "window",
      showWelcomeScreen: false,
      defaultLanguage: currentLanguage === "ar" ? "ar" : "en",
      loadPreviousSession: true,
      chatInputKey: "chatInput",
      chatSessionKey: "sessionId",
      initialMessages: [
        currentLanguage === "ar" ? "مرحباً! 👋" : "Hi there! 👋",
        currentLanguage === "ar"
          ? `أهلاً بك في دعم ${merchant?.name || "اشترك"}. كيف يمكنني مساعدتك؟`
          : `Welcome to ${merchant?.name || "Eshtarek"} support. How can I help you today?`
      ],
      i18n: {
        en: {
          title: "✨ Eshtarek Smart Assistant ✨",
          subtitle: `Welcome to ${merchant?.name || "Eshtarek"} customer support. We're here to help you 24/7 with any questions or issues.`,
          footer: `Powered by ${merchant?.name || "Eshtarek"} AI Assistant`,
          inputPlaceholder: "Type your message here..."
        },
        ar: {
          title: "✨ مساعد اشترك الذكي ✨",
          subtitle: `مرحباً بك في دعم عملاء ${merchant?.name || "اشترك"}. نحن هنا لمساعدتك على مدار الساعة طوال أيام الأسبوع`,
          footer: `مدعوم من مساعد ${merchant?.name || "اشترك"} الذكي`,
          inputPlaceholder: "اكتب رسالتك هنا..."
        }
      },
      enableStreaming: false,
      metadata: {
        merchantId: merchant?.id,
        language: currentLanguage,
        timestamp: Date.now()
      }
    };

    try {
      createChat(chatConfig);

      const root = document.documentElement;
      root.style.setProperty("--chat--color-primary", primaryColor);
      root.style.setProperty("--chat--color-primary-shade-50", primaryShade50);
      root.style.setProperty("--chat--color-primary-shade-100", primaryShade100);

      // Mark as initialized in Redux
      dispatch(setChatInitialized(true));
      dispatch(setLastMerchantId(merchant?.id));
    } catch (error) {
      console.warn("Chat widget failed:", error);
    }
  }, [merchant?.id, isInitialized, lastMerchantId, dispatch]);

  // Handle style updates separately
  useEffect(() => {
    if (!isInitialized || !merchant) return;

    const primaryColor = merchant?.primary_color || "#4F46E5";
    const primaryShade50 = merchant?.primary_color
      ? adjustColorBrightness(merchant.primary_color, -10)
      : "#6366F1";
    const primaryShade100 = merchant?.primary_color
      ? adjustColorBrightness(merchant.primary_color, -20)
      : "#7C3AED";

    const root = document.documentElement;
    root.style.setProperty("--chat--color-primary", primaryColor);
    root.style.setProperty("--chat--color-primary-shade-50", primaryShade50);
    root.style.setProperty("--chat--color-primary-shade-100", primaryShade100);
  }, [merchant?.primary_color, currentLanguage]);

  // Handle logout - reset chat when user logs out
  useEffect(() => {
    // Only trigger when user transitions from logged in to logged out
    if (prevLoggedInRef.current && !isLoggedIn && isInitialized) {
      // Clear n8n chat session storage
      localStorage.removeItem("chatInput");
      localStorage.removeItem("sessionId");
      sessionStorage.removeItem("chatInput");
      sessionStorage.removeItem("sessionId");

      // Remove existing chat widget from DOM
      const existingChat = document.querySelector("[data-n8n-chat]");
      if (existingChat) {
        existingChat.remove();
      }

      // Reset Redux chat state
      dispatch(resetChat());
    }

    // Update the ref for next render
    prevLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn, isInitialized, dispatch]);

  return null;
};

export default ChatWidget;
